import { App } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import {
    getApiError,
    getSchedulerGetPointsWithoutRoute,
    getSchedulerSearchPointsWithoutRoute,
} from "tm-shared/api";
import { Point, UserRole } from "tm-shared/types";

import { ROLE_CHILDREN_MAP } from "@/constants/domain/managerAgentVocabulary";
import { isManagerRole } from "@/utils/business/isManagerRole";

interface UsePointsWithoutRouteParams {
    areaId: string;
    role: UserRole;
    partnerId?: string;
}

export const usePointsWithoutRoute = ({
    areaId,
    role,
    partnerId,
}: UsePointsWithoutRouteParams) => {
    const { notification } = App.useApp();

    const [points, setPoints] = useState<Point[]>([]);
    const [loading, setLoading] = useState(false);

    const abortRef = useRef<AbortController | null>(null);
    const requestIdRef = useRef(0);

    const fetchPoints = useCallback(
        async (query?: string) => {
            if (!areaId) return;

            // 1) скасувати попередній запит
            abortRef.current?.abort();

            // 2) створити контролер для нового
            const controller = new AbortController();
            abortRef.current = controller;

            // 3) requestId щоб не перезатирати state старими відповідями
            const requestId = ++requestIdRef.current;

            setLoading(true);

            try {
                const commonParams = {
                    areaId,
                    roleSlug: ROLE_CHILDREN_MAP[role] ?? role,
                    clientId: isManagerRole(role) ? partnerId : undefined,
                };

                const axiosOverrides = {
                    axiosOverrides: { signal: controller.signal },
                };

                const apiPromise =
                    query && query?.length >= 4
                        ? getSchedulerSearchPointsWithoutRoute(
                              {
                                  query,
                                  ...commonParams,
                              },
                              axiosOverrides,
                          )
                        : getSchedulerGetPointsWithoutRoute(
                              commonParams,
                              axiosOverrides,
                          );

                const { result } = await apiPromise;

                // якщо це вже не останній запит — ігноруємо результат
                if (requestId !== requestIdRef.current) return;

                setPoints(result);
            } catch (error) {
                const { displayMessage, isAborted } = getApiError(error);

                if (isAborted) return;

                notification.error({
                    title: "Не вдалося отримати маркети",
                    description: displayMessage,
                });
            } finally {
                // 5) loading вимикаємо тільки для останнього запиту
                if (requestId === requestIdRef.current) {
                    setLoading(false);
                }
            }
        },
        [notification, areaId, role, partnerId],
    );

    useEffect(() => {
        fetchPoints();

        return () => {
            // скасувати запит при unmount
            abortRef.current?.abort();
        };
    }, [fetchPoints]);

    return { points, loading, refetch: fetchPoints };
};
